import { MealsMode, TimeSettings, breakModeLabel } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert, confirm } from "../../elements/alert-dialog";
import "../../elements/entity-filters";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { type RouteArgs, Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import "./components/time-settings-dialog";
import type { TimeSettingsDialog } from "./components/time-settings-dialog";

@customElement("ptc-settings-time")
export class SettingsTime extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/time/;

    get routeTitle() {
        return "Zeiterfassung | Einstellungen";
    }

    get helpPage() {
        return "handbuch/einstellungen/rechnungsstellung/";
    }

    handleRoute(_args: RouteArgs, { sid, ...rest }: { [params: string]: string }) {
        if (sid) {
            const settings = app.availableTimeSettings.find((s) => s.id === Number(sid));
            if (settings) {
                void this._editTimeSettings(settings);
            }
            this.router.params = rest;
        }
    }

    @state()
    private _loading = false;

    @singleton("ptc-time-settings-dialog")
    private _timeSettingsDialog: TimeSettingsDialog;

    private async _editTimeSettings(settings: TimeSettings) {
        const changes = await this._timeSettingsDialog.show(settings);
        if (changes) {
            // Update settings in the background
            void app.fetchCompany();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private async _newTimeSettings({ id, name, ...rest }: Partial<TimeSettings> = {}) {
        const added = await this._timeSettingsDialog.show(
            new TimeSettings({ ...rest, name: name ? name + " (Kopie)" : "" })
        );
        if (added) {
            // Update settings in the background
            void app.fetchCompany();
        }
    }

    private async _deleteTimeSettings(s: TimeSettings) {
        if (
            await confirm(
                "Sind Sie sicher dass Sie diesen Zeiterfassungs-Regelsatz löschen möchten?",
                "Löschen",
                "Abbrechen",
                { title: "Regelsatz Löschen", type: "destructive", icon: "icon.action.delete" }
            )
        ) {
            this._loading = true;
            try {
                await app.api.deleteTimeSettings(s.id);
                await app.fetchCompany();
            } catch (e) {
                void alert(e.message, { type: "warning" });
            }
            this._loading = false;
        }
    }

    private async _itemMoved({ detail: { item, toIndex } }: CustomEvent<{ item: TimeSettings; toIndex: number }>) {
        this._loading = true;
        item.order = toIndex;
        try {
            await app.api.createOrUpdateTimeSettings(item);
            await app.fetchCompany();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    static styles = [
        shared,
        css`
            .settings-item {
                padding: 0.75em 0.75em 0.4em 0.75em;
            }

            .settings-item .box {
                padding: 0.1em 0.3em;
                font-weight: 600;
                position: relative;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return;
        }
        return html`
            <div class="fullbleed vertical layout">
                <ptc-scroller class="stretch">
                    <div class="double-padded narrow-content">
                        <h2>Zeiterfassungs-Regelsätze</h2>
                        <div class="box">
                            <ptc-sortable-list
                                .items=${app.availableTimeSettings}
                                @item-moved=${this._itemMoved}
                                .renderItem=${(s: TimeSettings) => {
                                    return html`
                                        <div
                                            class="click spacing vertical layout settings-item border-bottom relative"
                                            @click=${() => this._editTimeSettings(s)}
                                        >
                                            <div class="larger">${s.name}</div>
                                            <div class="spacing wrapping smaller horizontal layout">
                                                <ptc-entity-filters
                                                    readonly
                                                    .filters=${s.filters}
                                                    hideFilterIcon
                                                    style="padding: 0; max-width: 100%;"
                                                    hideEmployeeCount
                                                ></ptc-entity-filters>
                                                <div
                                                    class="padded box centering spacing horizontal layout"
                                                    title="Pausenmodus"
                                                >
                                                    <ptc-ui-icon name="icon.object.break"></ptc-ui-icon>
                                                    <div>${breakModeLabel(s.breakMode)}</div>
                                                </div>

                                                <div
                                                    class="padded box centering horizontal layout"
                                                    title="Mitarbeiteressen"
                                                >
                                                    <ptc-ui-icon name="icon.object.meal"></ptc-ui-icon>
                                                    <div>
                                                        ${s.mealsMode === MealsMode.Auto
                                                            ? "Automatisch"
                                                            : s.mealsMode === MealsMode.Employee
                                                              ? "Durch Mitarbeiter"
                                                              : "Manuell"}
                                                    </div>
                                                </div>

                                                <div
                                                    class="padded box centering horizontal layout ${s.trackingEnabled
                                                        ? ""
                                                        : "strike-through"}"
                                                    title="Zeiterfassung per Stempeluhr"
                                                >
                                                    <ptc-ui-icon
                                                        name="icon.feature.time-tracking"
                                                        class="larger"
                                                    ></ptc-ui-icon>
                                                </div>

                                                ${s.trackingEnabled
                                                    ? html`
                                                          <div
                                                              class="padded box centering horizontal layout ${s.unplannedCheckins
                                                                  ? ""
                                                                  : "strike-through"}"
                                                              title="Ungeplante/spontane Schichten Erlauben"
                                                          >
                                                              <ptc-ui-icon
                                                                  name="icon.planning.shift-spontaneous"
                                                                  class="larger"
                                                              ></ptc-ui-icon>
                                                          </div>

                                                          <div
                                                              class="padded box centering horizontal layout ${s.trackingViaAppEnabled
                                                                  ? ""
                                                                  : "strike-through"}"
                                                              title="Zeiterfassung per Mitarbeiter-App"
                                                          >
                                                              <ptc-ui-icon
                                                                  name="icon.object.mobile-app"
                                                                  class="larger"
                                                              ></ptc-ui-icon>
                                                          </div>

                                                          <div
                                                              ?hidden=${!s.trackingViaAppEnabled}
                                                              class="padded box centering horizontal layout ${s.trackingViaAppRequiresToken
                                                                  ? ""
                                                                  : "strike-through"}"
                                                              title="Stempeln per MA-App erfordert QR-Code"
                                                          >
                                                              <ptc-ui-icon
                                                                  name="icon.action.scan-code"
                                                                  class="larger"
                                                              ></ptc-ui-icon>
                                                          </div>
                                                      `
                                                    : ""}
                                            </div>

                                            <button
                                                class="slim margined transparent top right absolute"
                                                @click=${(e: Event) => e.stopPropagation()}
                                            >
                                                <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                                            </button>

                                            <ptc-popover hide-on-leave hide-on-click>
                                                <div class="spacing vertical layout">
                                                    <button
                                                        class="slim transparent text-left-aligning"
                                                        @click=${(e: Event) => {
                                                            e.stopPropagation();
                                                            void this._editTimeSettings(s);
                                                        }}
                                                    >
                                                        <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> Bearbeiten
                                                    </button>
                                                    <button
                                                        class="slim transparent text-left-aligning"
                                                        @click=${(e: Event) => {
                                                            e.stopPropagation();
                                                            void this._newTimeSettings(s);
                                                        }}
                                                    >
                                                        <ptc-ui-icon name="icon.action.duplicate"></ptc-ui-icon>
                                                        Duplizieren
                                                    </button>
                                                    <button
                                                        class="slim transparent text-left-aligning"
                                                        @click=${(e: Event) => {
                                                            e.stopPropagation();
                                                            void this._deleteTimeSettings(s);
                                                        }}
                                                    >
                                                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon> Löschen
                                                    </button>
                                                </div>
                                            </ptc-popover>
                                        </div>
                                    `;
                                }}
                            ></ptc-sortable-list>

                            <div class="padded vertical layout">
                                <button class="transparent" @click=${this._newTimeSettings}>
                                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neuer Regelsatz
                                </button>
                            </div>
                        </div>
                    </div>
                </ptc-scroller>
            </div>
            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
